import { forwardRef, type Ref, useRef } from 'react'

import DefaultImage from '@space-shell/assets/images/default-image.png'

import { Content } from '@space-shell/common/components/imageSlider/content'
import { type SlideMediaProps } from '@space-shell/common/components/imageSlider/types'

import { SlideImgWrapper, SlideImg, SlideVideo } from './styles'
import { generateSourceTag, getSrcSetWeb } from './utils'

import { FileType } from '../enum'

const SlideImage = (
  {
    slide,
    isPreview = false,
    parentWidth,
    isMobile,
    hasContent,
    nextSlide,
    hasOneSlide,
    performance = {
      loading: 'lazy',
      fetchPriority: 'low'
    }
  }: SlideMediaProps,
  ref: Ref<HTMLDivElement>
) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleVideoLoop = () => {
    if (!hasOneSlide) {
      nextSlide()
      if (videoRef.current) {
        videoRef.current.currentTime = 0
      }
    }
  }

  if (
    !slide.imageUrlWeb &&
    !slide.imageUrlMobile &&
    !slide.videoUrlWeb &&
    !slide.videoUrlMobile &&
    !isPreview
  ) {
    return null
  }

  return (
    <SlideImgWrapper
      className='_w-full _h-full'
      $hasContent={Boolean(hasContent)}
      $isMobile={isMobile}
      $version={Number(slide.version)}>
      {slide.type === FileType.VIDEO ? (
        <SlideVideo
          data-test='banner-video'
          className='_w-full'
          ref={videoRef}
          src={
            isMobile
              ? !slide.videoUrlMobile
                ? slide.videoUrlWeb
                : slide.videoUrlMobile
              : slide.videoUrlWeb
          }
          muted
          autoPlay
          loop={hasOneSlide || isPreview}
          onEnded={handleVideoLoop}
          disablePictureInPicture
          playsInline
          webkit-playsInline></SlideVideo>
      ) : (
        <>
          {!slide.imageUrlWeb ? (
            <SlideImg
              className='_position-relative'
              src={DefaultImage}
              alt='image default'
              loading={performance.loading}
              fetchpriority={performance.fetchPriority}
              decoding='async'
              draggable={false}
              $hasContent={Boolean(hasContent)}
              $isImageMobile={Boolean(slide.imageUrlMobile)}
              $isMobile={isMobile}
              $version={Number(slide.version)}
            />
          ) : (
            <>
              {generateSourceTag(
                'image/webp',
                `${slide?.imageUrlWebP2 ? slide?.imageUrlWebP2 : slide?.imageUrlWebL2} 480w,
                ${slide?.imageUrlWebP3 ? slide?.imageUrlWebP3 : slide?.imageUrlWebL2} 640w,
                ${slide?.imageUrlWebP4 ? slide?.imageUrlWebP4 : slide?.imageUrlWebL2} 720w,`
              )}
              {generateSourceTag('image/jpg', getSrcSetWeb({ isPreview, slide }))}
              <SlideImg
                className='_position-relative'
                srcSet={getSrcSetWeb({ isPreview, slide })}
                src={slide.imageUrlWeb}
                alt={slide.altText}
                loading={performance.loading}
                fetchpriority={performance.fetchPriority}
                decoding='async'
                draggable={false}
                $hasContent={Boolean(hasContent)}
                $isImageMobile={Boolean(slide.imageUrlMobile)}
                $isMobile={isMobile}
                $version={Number(slide.version)}
              />
            </>
          )}
        </>
      )}

      <Content slide={slide} isMobile={isMobile} parentWidth={parentWidth} ref={ref} />
    </SlideImgWrapper>
  )
}

export default forwardRef(SlideImage)
